import ContentSaveIcon from '@mui/icons-material/Save';
import { Button as MuiButton, Stack } from '@mui/material';
import { FormEvent, useEffect, useMemo } from 'react';
import {
    RecordContextProvider,
    Toolbar,
    useCreate,
    useNotify,
    useUpdate,
} from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import type { MissionType, MissionTypeId } from '@wwf/sdk';
import { MissionTypeInput, missionTypeToInput } from '@domain/missionType';
import { resourceInputPayload } from '@domain/types';
import { MissionTypeInputs } from './MissionTypeInputs';

type DrawerMode = 'create' | 'edit' | 'show';

const defaultValues: MissionTypeInput = {
    id: '' as MissionTypeId,
    name: '',
    shortDescription: '',
    enabled: true,
    whoCanSee: 'VOLUNTEER',
    whoCanParticipate: 'VOLUNTEER',
    whoCanCreate: 'VOLUNTEER',
};

export function MissionTypeDrawerForm({
    mode,
    record,
    loading,
    onClose,
    onSuccess,
}: {
    mode: DrawerMode;
    record?: MissionType;
    loading: boolean;
    onClose(): void;
    onSuccess(): void;
}) {
    const notify = useNotify();
    const [create] = useCreate();
    const [update] = useUpdate();

    const form = useForm<MissionTypeInput>({
        defaultValues,
    });

    const formRecord = useMemo(
        () =>
            record && (mode === 'edit' || mode === 'show')
                ? missionTypeToInput(record)
                : undefined,
        [record, mode]
    );

    useEffect(() => {
        if (formRecord) {
            form.reset(formRecord);
        } else {
            form.reset(defaultValues);
        }
    }, [formRecord, form]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (mode === 'show') return;
        form.handleSubmit(async (data: MissionTypeInput) => {
            try {
                const payload: MissionTypeInput = {
                    id:
                        mode === 'create'
                            ? (String(data.id).trim() as MissionTypeId)
                            : record!.id,
                    name: data.name!.trim(),
                    shortDescription: data.shortDescription?.trim() || null,
                    enabled: data.enabled ?? true,
                    whoCanSee: data.whoCanSee,
                    whoCanParticipate: data.whoCanParticipate,
                    whoCanCreate: data.whoCanCreate,
                    ...resourceInputPayload(
                        { inputKey: 'iconInput', idKey: 'iconId' },
                        data.iconInput,
                        data.iconId
                    ),
                };

                if (mode === 'create') {
                    await create(
                        'missionType',
                        {
                            data: payload,
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                } else {
                    if (!record?.id) {
                        notify('Record not found', { type: 'error' });
                        return;
                    }
                    await update(
                        'missionType',
                        {
                            id: record.id,
                            data: payload,
                            previousData: record,
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                }
            } catch (e) {
                notify(String(e), { type: 'error' });
            }
        })(event);
    };

    const saving = form.formState.isSubmitting;
    const canSave = !loading && !saving;

    return (
        <FormProvider {...form}>
            <RecordContextProvider value={formRecord ?? undefined}>
                <form onSubmit={handleSubmit}>
                    <Stack gap={2}>
                        <MissionTypeInputs readOnly={mode === 'show'} />
                    </Stack>
                    {mode !== 'show' && (
                        <Toolbar sx={{ mt: 2, px: 0 }}>
                            <MuiButton
                                variant="contained"
                                type="submit"
                                disabled={!canSave}
                                startIcon={<ContentSaveIcon />}
                            >
                                Save
                            </MuiButton>
                            <MuiButton
                                variant="text"
                                onClick={onClose}
                                sx={{ ml: 1 }}
                                disabled={saving}
                            >
                                Cancel
                            </MuiButton>
                        </Toolbar>
                    )}
                </form>
            </RecordContextProvider>
        </FormProvider>
    );
}
